import { FormControl, FormLabel, Select, VStack } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { getEffectiveServices } from '../../../../../utils/firebaseServiceContext'

interface Props {
  classification?: 'sporadic' | 'familial' | undefined
  familialGene?: string | undefined
  onChangeClassification: (value?: 'sporadic' | 'familial') => void
  onChangeFamilialGene: (value?: string) => void
  isRequired?: boolean
}

const AlsOnsetClassification = ({
  classification,
  familialGene,
  onChangeClassification,
  onChangeFamilialGene,
  isRequired = false,
}: Props) => {
  const [genes, setGenes] = useState<Record<string, string>>({})

  useEffect(() => {
    const fetchGenes = async () => {
      const ref = doc(
        getEffectiveServices().firestore,
        'config',
        'familial-gene',
      )
      const snap = await getDoc(ref)
      if (snap.exists()) {
        const data = snap.data()
        const { value = {}, archivedIds = [] } = data
        const filteredValue = Object.keys(value)
          .filter((key) => !archivedIds.includes(key))
          .reduce((acc, key) => {
            acc[key] = value[key]
            return acc
          }, {} as Record<string, string>)
        setGenes(filteredValue)
      }
    }
    fetchGenes()
  }, [])

  const sortedGenes = useMemo(() => {
    return Object.entries(genes).sort(([, a], [, b]) => {
      const aa = a.replace(/^\d+\.\s*/, '').toLowerCase()
      const bb = b.replace(/^\d+\.\s*/, '').toLowerCase()
      return aa.localeCompare(bb)
    })
  }, [genes])

  return (
    <VStack align="stretch">
      <FormControl isRequired={isRequired}>
        <FormLabel>Onset classification</FormLabel>
        <Select
          placeholder="Select option"
          value={classification ?? ''}
          onChange={(e) => {
            const v = e.target.value
            if (!v) onChangeClassification(undefined)
            else onChangeClassification(v as 'sporadic' | 'familial')
          }}
        >
          <option value="sporadic">Sporadic</option>
          <option value="familial">Familial</option>
        </Select>
      </FormControl>

      {classification === 'familial' && (
        <FormControl isRequired>
          <FormLabel>Known familial gene</FormLabel>
          <Select
            placeholder="Select gene"
            value={familialGene ?? ''}
            onChange={(e) => {
              const v = e.target.value
              if (!v) onChangeFamilialGene(undefined)
              else onChangeFamilialGene(v)
            }}
          >
            {sortedGenes.map(([key, optionValue]) => {
              const display = optionValue.replace(/^\d+\.\s*/, '')
              return (
                <option key={key} value={key}>
                  {display}
                </option>
              )
            })}
          </Select>
        </FormControl>
      )}
    </VStack>
  )
}

export default AlsOnsetClassification
